type AiAgentDebugPromptOptions = {
  /** The id of the conversation the reply belongs to. */
  csid: string;
  /** The id of the message the AI agent authored. */
  messageId: string;
  /** When the reply was sent. */
  date?: string;
  /** What the agent said was wrong with the reply. */
  note?: string;
};

/**
 * Builds the prompt that opens a debugging session for a single AI agent reply.
 * The agent's own words are fenced and ruled off from the instructions around
 * them. The fence is not escaped, so it holds for prose, not for input that
 * sets out to break it.
 */
export const createAiAgentDebugPrompt = ({
  csid,
  messageId,
  date,
  note,
}: AiAgentDebugPromptOptions): string => {
  const report = note?.trim();

  return [
    [
      `Debug the AI agent reply in Dixa conversation ${csid}.`,
      date
        ? `The reply was sent at ${date} (message id ${messageId}).`
        : `The reply has message id ${messageId}.`,
    ].join('\n'),
    report && `**Analysis goal:**\n<agent_report>${report}\n</agent_report>`,
    'Explain why the AI agent answered this way, which knowledge, tools and instructions it used.',
  ]
    .filter(Boolean)
    .join('\n\n');
};

type AiAgentToolCallDebugPromptOptions = {
  /** The id of the conversation the tool call belongs to. */
  csid: string;
  /** The name of the tool the AI agent called. */
  toolName: string;
  /** When the tool was called. */
  date: string;
};

/** Builds the prompt that opens a debugging session for a single AI agent tool call. */
export const createAiAgentToolCallDebugPrompt = ({
  csid,
  toolName,
  date,
}: AiAgentToolCallDebugPromptOptions): string =>
  [
    [
      `Debug the AI agent tool call in Dixa conversation ${csid}.`,
      `The AI agent called the tool "${toolName}" at ${date}.`,
    ].join('\n'),
    'Explain why the AI agent chose to call this tool at this point, which instructions, knowledge and conversation context led to it, and the input it passed.',
  ].join('\n\n');
